import type { TFunction } from 'i18next';
import { z } from 'zod';
import { LimitedCompanyLocation } from 'ogp/services/company-service.types';

const isLimitedCompanyLocation = (value: unknown): value is LimitedCompanyLocation =>
  typeof value === 'object' &&
  value !== null &&
  'address' in value &&
  typeof value.address === 'string' &&
  value.address.trim().length > 0;

export const createLimitedCompanySchema = (t: TFunction) => {
  const requiredMessage = t('form.validation.required', { ns: 'web-common' });
  const invalidEmailMessage = t('form.validation.invalid-email', { ns: 'web-common' });

  return z.object({
    cocNumber: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage)
      .regex(/^\d{8}$/, t('collegial-lending.customers.create.validation.coc-number-invalid')),
    companyName: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage),
    location: z.custom<LimitedCompanyLocation>(isLimitedCompanyLocation, {
      message: requiredMessage,
    }),
    invoiceEmail: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage)
      .email(invalidEmailMessage),
    profileImageUrl: z.string(),
    adminFirstName: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage),
    adminLastName: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage),
    adminEmail: z
      .string({
        required_error: requiredMessage,
      })
      .min(1, requiredMessage)
      .email(invalidEmailMessage),
  });
};

export type CreateLimitedCompanyForm = z.infer<ReturnType<typeof createLimitedCompanySchema>>;
